import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Button, Image, message, Modal, Radio, Typography, Upload } from "antd";

import {
  DeleteOutlined,
  DownOutlined,
  DragOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  UpOutlined,
} from "@ant-design/icons";
import Swal from "sweetalert2";

import {
  createBrandContent,
  deleteBrandContent,
  getBrandContentsByType,
} from "../../../apis/BrandContentApi";
import { setLoading } from "../../../redux/store/common/commonDuck";
import { plutusS3 } from "../../../utils/plutusS3";

const { Text } = Typography;

export default function BannerManagement({
  selectedBrand,
  contentType,
  showMobile = true,
  canUserVideo = true,
}) {
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.common.loading);
  const setIsLoading = (value) => dispatch(setLoading(value));

  // 홈 배너 관리 상태
  const [banners, setBanners] = useState([]);
  const [bannerPreviewVisible, setBannerPreviewVisible] = useState(false);
  const [previewContent, setPreviewContent] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");
  const [previewType, setPreviewType] = useState("image");

  // 저장 시 URL 뒤에 붙은 timestamp 등 쿼리스트링 제거
  const stripQuery = (url) =>
    typeof url === "string" ? url.split("?")[0] : url;

  /**
   * 배너 데이터 조회
   */
  useEffect(() => {
    const fetchBanners = async () => {
      if (!selectedBrand?.id) return;
      setBanners([]);
      const param = {
        brandId: selectedBrand.id,
        contentType: contentType,
      };
      const res = await getBrandContentsByType(param);

      // 기존 브랜드 콘텐츠가 있으면 배너 리스트에 세팅
      if (res && res.length > 0) {
        const existingBanners = res.map((content, index) => {
          // 기존 timestamp 제거 (있다면)
          const cleanUrl = content.contentUrl.split("?")[0];
          // PC 이미지 URL에서 모바일 URL 생성 (_mobile 추가)
          const mobileUrl = cleanUrl + "_mobile";
          const timestamp = Date.now();
          return {
            id: content.id,
            type: content.mediaType.toLowerCase(), // 'IMAGE' -> 'image'
            order: content.orderValue,
            pcFile: null, // 기존 파일은 없고 URL만 있음
            mobileFile: null, // 기존 파일은 없고 URL만 있음
            pcPreview: cleanUrl + "?timestamp=" + timestamp,
            mobilePreview: mobileUrl + "?timestamp=" + timestamp,
            isExisting: true, // 기존 데이터임을 표시
          };
        });
        setBanners(existingBanners);
      }
    };
    fetchBanners();
  }, [selectedBrand]);

  /**
   * 배너 추가
   */
  const addBanner = () => {
    const newBanner = {
      id: Date.now(),
      type: "image", // 'image' 또는 'video'
      order: banners.length + 1,
      pcFile: null,
      mobileFile: null,
      pcPreview: null,
      mobilePreview: null,
      isExisting: false, // 신규 배너임을 표시
    };
    setBanners([...banners, newBanner]);
  };

  /**
   * 배너 삭제
   */
  const deleteBanner = async (bannerId) => {
    try {
      const res = await deleteBrandContent(bannerId);

      // 삭제 성공하면 로컬에서 해당 배너 제거
      // 로컬 상태에서 삭제된 배너 제거 및 순서 재정렬
      const updatedBanners = banners
        .filter((banner) => banner.id !== bannerId)
        .map((banner, index) => ({
          ...banner,
          order: index + 1,
        }));
      setBanners(updatedBanners);
      message.success("배너가 삭제되었습니다.");
    } catch (error) {
      console.error("배너 삭제 중 오류:", error);
      message.error("배너 삭제에 실패했습니다.");
    }
  };

  const removeBanner = (bannerId) => {
    Swal.fire({
      title: "배너를 삭제하시겠습니까?",
      text: "삭제된 배너는 복구할 수 없습니다.",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "rgb(147 147 147)",
      confirmButtonText: "삭제",
      cancelButtonText: "취소",
    }).then((result) => {
      if (result.isConfirmed) {
        deleteBanner(bannerId);
      }
    });
  };

  /**
   * 배너 타입 변경
   */
  const updateBannerType = (bannerId, type) => {
    setBanners(
      banners.map((banner) =>
        banner.id === bannerId
          ? {
              ...banner,
              type,
              pcFile: null,
              mobileFile: null,
              pcPreview: null,
              mobilePreview: null,
              // isExisting 상태는 유지
            }
          : banner,
      ),
    );
  };

  /**
   * 배너 순서 변경
   */
  const moveBanner = (bannerId, direction) => {
    const bannerIndex = banners.findIndex((banner) => banner.id === bannerId);
    const newIndex = direction === "up" ? bannerIndex - 1 : bannerIndex + 1;

    if (newIndex >= 0 && newIndex < banners.length) {
      const newBanners = [...banners];
      [newBanners[bannerIndex], newBanners[newIndex]] = [
        newBanners[newIndex],
        newBanners[bannerIndex],
      ];

      // 순서 번호 업데이트
      const updatedBanners = newBanners.map((banner, index) => ({
        ...banner,
        order: index + 1,
      }));

      setBanners(updatedBanners);
    }
  };

  /**
   * 파일 업로드 처리
   */
  const handleFileUpload = (file, bannerId, deviceType) => {
    const isValidType = (file, type) => {
      if (type === "image") {
        return file.type.startsWith("image/");
      } else if (type === "video") {
        return file.type.startsWith("video/");
      }
      return false;
    };

    const banner = banners.find((b) => b.id === bannerId);
    if (!isValidType(file, banner.type)) {
      message.error(
        `${
          banner.type === "image" ? "이미지" : "비디오"
        } 파일만 업로드 가능합니다.`,
      );
      return false;
    }

    const fileUrl = URL.createObjectURL(file);

    setBanners(
      banners.map((banner) =>
        banner.id === bannerId
          ? {
              ...banner,
              [`${deviceType}File`]: file,
              [`${deviceType}Preview`]: fileUrl,
            }
          : banner,
      ),
    );

    return false; // 자동 업로드 방지
  };

  /**
   * 배너 미리보기
   */
  const previewBanner = (banner, deviceType = "pc") => {
    const previewUrl =
      deviceType === "pc" ? banner.pcPreview : banner.mobilePreview;

    if (previewUrl) {
      setPreviewContent(previewUrl);
      setPreviewType(banner.type);
      setPreviewTitle(
        `배너 ${banner.order} - ${deviceType === "pc" ? "PC" : "모바일"}`,
      );
      setBannerPreviewVisible(true);
    } else {
      message.warning("미리보기할 콘텐츠가 없습니다.");
    }
  };

  /**
   * 배너 편집 (파일 재업로드)
   */
  const editBanner = (e, banner, deviceType = "pc") => {
    e.stopPropagation(); // 이벤트 전파 중지

    // 파일 입력 엘리먼트 생성
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = banner.type === "image" ? "image/*" : "video/*";
    fileInput.style.display = "none";

    fileInput.onchange = (event) => {
      const file = event.target.files[0];
      if (file) {
        handleFileUpload(file, banner.id, deviceType);
      }
    };

    document.body.appendChild(fileInput);
    fileInput.click();
    document.body.removeChild(fileInput);
  };

  // S3 업로드
  const s3Upload = async (file, order, deviceType) => {
    try {
      let s3Path = "";
      const timestamp = Date.now();

      // 홈 배너 경로 설정 (ProductRegisterComponent와 동일한 형식)
      if (deviceType === "pc") {
        s3Path = `brand/${selectedBrand.brand_name_en}/${contentType}/banner_${order}`;
      } else if (deviceType === "mobile") {
        s3Path = `brand/${selectedBrand.brand_name_en}/${contentType}/banner_${order}_mobile`;
      }

      let params = {
        Bucket: process.env.REACT_APP_S3_BUCKETNAME,
        Key: s3Path,
        Body: file,
      };

      const res = await plutusS3.putObject(params).promise();

      // ProductRegisterComponent와 동일한 성공 확인 방식
      if (res.success === true) {
        // S3 URL 생성
        const s3Url = `https://${process.env.REACT_APP_S3_BUCKETNAME}.s3.${process.env.REACT_APP_S3_REGION}.amazonaws.com/${s3Path}`;
        return s3Url;
      }
      return null;
    } catch (error) {
      console.error("S3 업로드 오류:", error);
      throw error;
    }
  };

  const saveBannerContent = async () => {
    try {
      // 배너 유효성 검사
      if (banners.length === 0) {
        message.warning("최소 1개의 배너를 추가해주세요.");
        return;
      }

      for (const banner of banners) {
        // 기존 배너는 URL이 있으면 OK, 신규 배너는 파일이 있어야 함
        if (banner.isExisting) {
          if (!banner.pcPreview) {
            message.warning(`배너 ${banner.order}의 PC 콘텐츠가 없습니다.`);
            return;
          }
        } else {
          // if (!banner.pcFile || !banner.mobileFile) {
          //   message.warning(
          //     `배너 ${banner.order}의 PC/모바일 파일을 모두 업로드해주세요.`,
          //   );
          //   return;
          // }
        }
      }

      if (!selectedBrand) {
        message.warning("브랜드를 선택해주세요.");
        return;
      }

      setIsLoading(true);

      // 배너 순서를 현재 배열 순서에 맞게 1부터 재정렬
      const reorderedBanners = banners.map((banner, index) => ({
        ...banner,
        order: index + 1,
      }));

      // 각 배너에 대해 순차적으로 처리
      for (const banner of reorderedBanners) {
        try {
          let pcContentUrl = banner.pcPreview;
          let mobileContentUrl = banner.mobilePreview;

          // 기존 배너이지만 파일이 새로 업로드된 경우에만 S3 업로드
          if (banner.pcFile) {
            pcContentUrl = await s3Upload(banner.pcFile, banner?.order, "pc");
            if (!pcContentUrl) {
              throw new Error(`배너 ${banner.order} PC 파일 업로드 실패`);
            }
          }

          if (banner.mobileFile) {
            mobileContentUrl = await s3Upload(
              banner.mobileFile,
              banner?.order,
              "mobile",
            );
            if (!mobileContentUrl) {
              throw new Error(`배너 ${banner.order} 모바일 파일 업로드 실패`);
            }
          }

          // 저장 시에는 쿼리스트링이 섞이지 않도록 정제
          pcContentUrl = stripQuery(pcContentUrl);
          mobileContentUrl = stripQuery(mobileContentUrl);

          // API 저장 - upsert 방식 (기존 배너면 수정, 신규면 생성)
          const param = {
            id: banner.isExisting ? banner.id : null, // 기존 배너면 id 포함, 신규면 null
            brandId: selectedBrand.id,
            contentType: contentType,
            mediaType: banner.type.toUpperCase(),
            contentUrl: pcContentUrl,
            title: null,
            subTitle: null,
            description: null,
            linkUrl: null,
            orderValue: banner.order,
            isDeleted: false,
          };

          const result = await createBrandContent(param);
        } catch (bannerError) {
          console.error(`❌ 배너 ${banner.order} 저장 실패:`, bannerError);
          message.error(`배너 ${banner.order} 저장에 실패했습니다.`);
          return; // 하나라도 실패하면 중단
        }
      }
      message.success("모든 배너가 성공적으로 저장되었습니다.");

      // 저장 후 데이터 다시 조회
      const param = {
        brandId: selectedBrand.id,
        contentType: contentType,
      };
      const res = await getBrandContentsByType(param);

      // 배너 리스트 다시 세팅
      if (res && res.length > 0) {
        const updatedBanners = res.map((content) => {
          // 기존 timestamp 제거 (있다면)
          const cleanUrl = content.contentUrl.split("?")[0];
          const mobileUrl = cleanUrl + "_mobile";
          const timestamp = Date.now();
          return {
            id: content.id,
            type: content.mediaType.toLowerCase(),
            order: content.orderValue,
            pcFile: null,
            mobileFile: null,
            pcPreview: cleanUrl + "?timestamp=" + timestamp,
            mobilePreview: mobileUrl + "?timestamp=" + timestamp,
            isExisting: true,
          };
        });
        setBanners(updatedBanners);
      } else {
        // 데이터가 없으면 빈 배열로 설정
        setBanners([]);
      }
    } catch (error) {
      message.error("배너 저장 중 오류가 발생했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="banner-section">
        {/* 헤더 */}
        <div className="banner-section__header">
          <div>
            <h2 className="banner-section__header-title">🎨 배너 관리</h2>
            <p className="banner-section__header-description">
              이미지/비디오를 혼용하여 여러 배너를 등록할 수 있습니다
            </p>
          </div>
          <div>
            <Button
              type="primary"
              onClick={saveBannerContent}
              className="save-button"
              loading={isLoading}
            >
              배너 수정/저장
            </Button>
            <Button
              type="default"
              icon={<PlusOutlined />}
              onClick={addBanner}
              style={{ marginLeft: 10 }}
            >
              배너 추가
            </Button>
          </div>
        </div>

        {/* 배너 리스트 */}
        {banners.length === 0 ? (
          <button
            type="button"
            style={{ display: "block", width: "100%" }}
            className="banner-section__empty-state"
            onClick={addBanner}
          >
            <PlusOutlined className="banner-section__empty-state-icon" />
            <div className="banner-section__empty-state-title">
              배너를 추가해보세요
            </div>
            <div className="banner-section__empty-state-subtitle">
              이미지와 비디오를 혼용하여 배너를 생성할 수 있습니다
            </div>
          </button>
        ) : (
          <div className="banner-section__list">
            {banners.map((banner, index) => (
              <div key={banner.id} className="banner-item">
                {/* 배너 헤더 */}
                <div className="banner-item__header">
                  <div className="banner-item__header-left">
                    <DragOutlined className="banner-item__header-left-drag" />
                    <span className="banner-item__header-left-title">
                      배너 {banner.order}
                    </span>
                    <Radio.Group
                      value={banner.type}
                      onChange={(e) =>
                        updateBannerType(banner.id, e.target.value)
                      }
                      size="small"
                    >
                      <Radio.Button value="image">이미지</Radio.Button>
                      <Radio.Button value="video" disabled={!canUserVideo}>
                        비디오
                      </Radio.Button>
                    </Radio.Group>
                  </div>

                  <div className="banner-item__header-right">
                    <Button
                      size="small"
                      icon={<UpOutlined />}
                      disabled={index === 0}
                      onClick={() => moveBanner(banner.id, "up")}
                    />
                    <Button
                      size="small"
                      icon={<DownOutlined />}
                      disabled={index === banners.length - 1}
                      onClick={() => moveBanner(banner.id, "down")}
                    />
                    <Button
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => removeBanner(banner.id)}
                    />
                  </div>
                </div>

                {/* 업로드 영역 */}
                <div className="banner-item__upload-area">
                  {/* PC 업로드 */}
                  <div className="banner-item__upload-section">
                    <div className="banner-item__upload-section-title">
                      <Text strong>PC (1920*820)</Text>
                    </div>
                    {banner.pcPreview ? (
                      // 업로드된 파일이 있을 때 - Upload 컴포넌트 없이 표시
                      <div className="upload-preview upload-preview--pc">
                        {banner.type === "image" ? (
                          <Image
                            src={banner.pcPreview}
                            alt="PC 배너"
                            className="upload-preview__media"
                            preview={false}
                          />
                        ) : (
                          <video
                            src={banner.pcPreview}
                            className="upload-preview__media"
                            controls={false}
                            muted
                          />
                        )}
                        <Button
                          icon={<EditOutlined />}
                          onClick={(e) => editBanner(e, banner, "pc")}
                          className="upload-preview__button"
                        />
                        <Button
                          icon={<EyeOutlined />}
                          onClick={() => previewBanner(banner, "pc")}
                          className="upload-preview__button"
                          style={{ right: "38px" }}
                        />
                      </div>
                    ) : (
                      // 업로드할 파일이 없을 때 - Upload 컴포넌트 사용
                      <Upload
                        listType="picture-card"
                        showUploadList={false}
                        beforeUpload={(file) =>
                          handleFileUpload(file, banner.id, "pc")
                        }
                        accept={banner.type === "image" ? "image/*" : "video/*"}
                        style={{ width: "100%" }}
                      >
                        <div className="upload-empty upload-empty--pc">
                          <PlusOutlined className="upload-empty__icon upload-empty__icon--pc" />
                          <div className="upload-empty__text upload-empty__text--pc">
                            PC {banner.type === "image" ? "이미지" : "비디오"}{" "}
                            업로드
                          </div>
                          <div className="upload-empty__ratio upload-empty__ratio--pc">
                            16:9 비율
                          </div>
                        </div>
                      </Upload>
                    )}
                  </div>

                  {/* 모바일 업로드 */}
                  {showMobile && (
                    <div className="banner-item__upload-section">
                      <div className="banner-item__upload-section-title">
                        <Text strong>모바일 (430*618)</Text>
                      </div>
                      {banner.mobilePreview ? (
                        // 업로드된 파일이 있을 때 - Upload 컴포넌트 없이 표시
                        <div className="upload-preview upload-preview--mobile">
                          {banner.type === "image" ? (
                            <Image
                              src={banner.mobilePreview}
                              alt="모바일 배너"
                              className="upload-preview__media"
                              preview={false}
                            />
                          ) : (
                            <video
                              src={banner.mobilePreview}
                              className="upload-preview__media"
                              controls={false}
                              muted
                            />
                          )}
                          <Button
                            icon={<EditOutlined />}
                            onClick={(e) => editBanner(e, banner, "mobile")}
                            className="upload-preview__button"
                          />
                          <Button
                            icon={<EyeOutlined />}
                            onClick={() => previewBanner(banner, "mobile")}
                            className="upload-preview__button"
                            style={{ right: "38px" }}
                          />
                        </div>
                      ) : (
                        // 업로드할 파일이 없을 때 - Upload 컴포넌트 사용
                        <Upload
                          listType="picture-card"
                          showUploadList={false}
                          beforeUpload={(file) =>
                            handleFileUpload(file, banner.id, "mobile")
                          }
                          accept={
                            banner.type === "image" ? "image/*" : "video/*"
                          }
                          style={{ width: "100%" }}
                        >
                          <div className="upload-empty upload-empty--mobile">
                            <PlusOutlined className="upload-empty__icon upload-empty__icon--mobile" />
                            <div className="upload-empty__text upload-empty__text--mobile">
                              모바일{" "}
                              {banner.type === "image" ? "이미지" : "비디오"}{" "}
                              업로드
                            </div>
                            <div className="upload-empty__ratio upload-empty__ratio--mobile">
                              9:16 비율
                            </div>
                          </div>
                        </Upload>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 배너 미리보기 모달 */}
      <Modal
        visible={bannerPreviewVisible}
        title={previewTitle}
        footer={null}
        onCancel={() => setBannerPreviewVisible(false)}
        width={1000}
        centered
      >
        {previewType === "image" ? (
          <Image
            src={previewContent}
            alt="배너 미리보기"
            style={{ width: "100%" }}
          />
        ) : (
          <video
            src={previewContent}
            style={{ width: "100%", aspectRatio: "16/9" }}
            controls
            autoPlay
          />
        )}
      </Modal>
    </>
  );
}
